import { useMutation, useQuery } from "@tanstack/react-query";
import { Badge } from "@veil/ui/components/badge";
import { Button } from "@veil/ui/components/button";
import ArrowLeft from "lucide-react/dist/esm/icons/arrow-left";
import ExternalLink from "lucide-react/dist/esm/icons/external-link";
import Maximize from "lucide-react/dist/esm/icons/maximize";
import RefreshCcw from "lucide-react/dist/esm/icons/refresh-ccw";
import Trash2 from "lucide-react/dist/esm/icons/trash-2";
import { useCallback, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { Link, useLocation, useSearchParams } from "wouter";
import {
	StopSessionDialog,
	type StopSessionTarget,
} from "../components/workers/stop-session-dialog";
import { api, unwrap } from "../lib/api";
import { withControlToken } from "../lib/session-access";

type ViewTab = "devtools" | "vnc";

interface ViewTicket {
	ticket: string;
	expiresIn: number;
	hostname: string;
	vncUrl: string;
	devtoolsUrl: string;
	/**
	 * The per-launch capability the worker's `/browsers/*` routes require.
	 * Absent from a worker older than that change, whose routes need none.
	 */
	controlToken?: string;
	controlTokenParam?: string;
	controlExpiresIn?: number;
}

/**
 * One session, two ways to look at it, and as little chrome as we can get away
 * with: everything that is not the viewport is competing with it.
 *
 * DevTools carries most of the load — its screencast panel shows the page and
 * lets you drive it. VNC is the escape hatch for what lives outside the page
 * viewport: native dialogs, extension popups, file pickers.
 *
 * Both tabs frame the worker's own shell rather than the raw surfaces, so the
 * session facts and the utility bar come along. The shell drops its header when
 * it detects it is framed, leaving this toolbar as the only chrome up top.
 */
export function SessionViewer({
	hostname,
	launchId,
}: {
	hostname: string;
	launchId: string;
}) {
	const [, navigate] = useLocation();
	const [searchParams] = useSearchParams();
	const initial: ViewTab =
		searchParams.get("view") === "vnc" ? "vnc" : "devtools";
	const [tab, setTab] = useState<ViewTab>(initial);
	const frameRef = useRef<HTMLIFrameElement>(null);
	// Bumped by Reconnect. A re-mint inside the same second produces a byte-
	// identical token, so the src alone would not tell React to reload.
	const [reloadKey, setReloadKey] = useState(0);

	// Minted server-side so the worker API key never reaches the browser.
	//
	// Fetched once, not on a timer. What the frames actually run on is the
	// control capability, which the worker turns into a twelve-hour path-scoped
	// cookie the moment the first frame loads; a ten-minute poll would mint a
	// fresh capability pair for a session that already has access, and
	// rewriting a live frame's src reloads the viewer out from under whoever is
	// watching it. Renewal is the Reconnect button below — deliberate, and
	// nothing else in the app can trigger it (see affectedQueryRoots, which
	// keeps `view-ticket` off every invalidation list for the same reason).
	const ticket = useQuery({
		queryKey: ["view-ticket", hostname, launchId],
		queryFn: async () => {
			const res = (await unwrap(
				api.api
					.workers({ hostname })
					.browsers({ launchId })
					["view-ticket"].post(),
			)) as unknown as { data: ViewTicket };
			return res.data;
		},
		staleTime: Number.POSITIVE_INFINITY,
		retry: 1,
	});

	// Confirmed rather than immediate: this button sits in a toolbar next to
	// Reconnect and Fullscreen, and it destroys the session the operator is
	// currently watching.
	const [confirmKill, setConfirmKill] = useState<StopSessionTarget | null>(
		null,
	);

	const kill = useMutation({
		mutationFn: async () =>
			unwrap(
				api.api
					.workers({ hostname })
					.sessions.stop.post({ sessionId: launchId }),
			),
		onSuccess: () => {
			toast.success("Session stopped");
			navigate("/sessions");
		},
		onError: (err: Error) => toast.error(err.message),
		onSettled: () => setConfirmKill(null),
	});

	// The capability rides in the URL exactly once: the worker's shell absorbs
	// it into an HttpOnly cookie and redirects to the clean address, so it
	// leaves the frame's address, the client's history and any Referer a page
	// inside the session sends.
	const src = useMemo(() => {
		const data = ticket.data;
		if (!data) return undefined;
		return withControlToken(
			tab === "vnc" ? data.vncUrl : data.devtoolsUrl,
			data.controlToken,
			data.controlTokenParam,
		);
	}, [tab, ticket.data]);

	const goFullscreen = useCallback(() => {
		void frameRef.current?.requestFullscreen?.();
	}, []);

	// The one place a fresh capability is minted after the first load: for a
	// watch long enough to outlive the token, or a frame that lost the session
	// and needs to re-enter it.
	const refetchTicket = ticket.refetch;
	const reconnect = useCallback(() => {
		setReloadKey((n) => n + 1);
		void refetchTicket();
	}, [refetchTicket]);

	return (
		// Forced dark, scoped to this route. The rest of the console is light,
		// but this page is nothing but a frame around a viewer that renders dark
		// either way, and light chrome around a dark viewport reads as a seam.
		<div className="dark flex h-svh flex-col bg-background text-foreground">
			<header className="flex h-9 shrink-0 items-center gap-2 border-b border-border/60 px-2">
				<Button asChild size="xs" variant="ghost" aria-label="Back to sessions">
					<Link href="/sessions">
						<ArrowLeft aria-hidden />
					</Link>
				</Button>

				<div
					className="flex items-center gap-0.5"
					role="tablist"
					aria-label="Viewer"
				>
					{(["devtools", "vnc"] as const).map((value) => (
						<Button
							key={value}
							size="xs"
							variant={tab === value ? "outline" : "ghost"}
							role="tab"
							aria-selected={tab === value}
							onClick={() => setTab(value)}
						>
							{value === "devtools" ? "DevTools" : "VNC"}
						</Button>
					))}
				</div>

				<span className="truncate font-mono text-xs text-muted-foreground">
					{hostname} / {launchId.slice(0, 8)}
				</span>

				{ticket.isError ? (
					<Badge variant="destructive">Ticket failed</Badge>
				) : null}

				<div className="ml-auto flex items-center gap-0.5">
					<Button
						size="xs"
						variant="ghost"
						disabled={ticket.isFetching}
						onClick={reconnect}
						aria-label="Reconnect, renewing access to this session"
					>
						<RefreshCcw aria-hidden />
					</Button>
					<Button
						size="xs"
						variant="ghost"
						onClick={goFullscreen}
						aria-label="Fullscreen"
					>
						<Maximize aria-hidden />
					</Button>
					<Button
						size="xs"
						variant="ghost"
						disabled={!src}
						onClick={() => src && window.open(src, "_blank", "noopener")}
						aria-label="Open in a new tab"
					>
						<ExternalLink aria-hidden />
					</Button>
					<Button
						size="xs"
						variant="ghost"
						disabled={kill.isPending}
						onClick={() =>
							setConfirmKill({ kind: "launch", hostname, id: launchId })
						}
						aria-label="Stop this session"
					>
						<Trash2 aria-hidden />
					</Button>
				</div>
			</header>

			<div className="min-h-0 flex-1">
				{ticket.isLoading ? (
					<div className="grid h-full place-items-center text-sm text-muted-foreground">
						Requesting access…
					</div>
				) : ticket.isError ? (
					<div className="grid h-full place-items-center px-6 text-center">
						<div>
							<p className="text-sm font-medium">Could not open this session</p>
							<p className="mt-1 max-w-md text-sm text-muted-foreground">
								{(ticket.error as Error).message}. It may have ended, or the
								worker may be unreachable.
							</p>
						</div>
					</div>
				) : (
					<iframe
						ref={frameRef}
						key={`${tab}:${reloadKey}`}
						src={src}
						title={tab === "vnc" ? "Session VNC" : "Session DevTools"}
						className="size-full border-0"
						allow="clipboard-read; clipboard-write; fullscreen"
					/>
				)}
			</div>

			<StopSessionDialog
				target={confirmKill}
				pending={kill.isPending}
				onOpenChange={() => setConfirmKill(null)}
				onConfirm={() => kill.mutate()}
			/>
		</div>
	);
}
